import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { apiThunk } from "App/store/apiThunk";

/**
 * The agency's vehicles: the fleet register (§5.2) and the live fleet view
 * (§5.3) from TENANT_AGENCY_MODULE.md.
 *
 * Only a car with a currently assigned, in-window driver has a live position —
 * position is recorded against the driver, not the vehicle. A car parked with
 * no driver assigned is untracked, not "at its last known location", so the UI
 * reads `last_seen` to distinguish a live fix from a stale one.
 */

interface AgencyArg {
  agencyId: number | string;
}

export const loadVehicles = apiThunk<any, AgencyArg>("fleet/loadVehicles", {
  server: "login",
  url: ({ agencyId }) => `tenant-agencies/${agencyId}/vehicles`,
  method: "GET",
  errorMsg: true,
});

export const addVehicle = apiThunk<any, AgencyArg & { vehicle: Record<string, any> }>(
  "fleet/addVehicle",
  {
    server: "login",
    url: ({ agencyId }) => `tenant-agencies/${agencyId}/vehicles`,
    method: "POST",
    body: ({ vehicle }) => vehicle,
    successMsg: true,
    errorMsg: true,
  }
);

export const removeVehicle = apiThunk<any, AgencyArg & { vehicleId: number | string }>(
  "fleet/removeVehicle",
  {
    server: "login",
    url: ({ agencyId, vehicleId }) => `tenant-agencies/${agencyId}/vehicles/${vehicleId}`,
    method: "DELETE",
    successMsg: true,
    errorMsg: true,
  }
);

/** One row per agency car: driver, position, status and last-seen time. */
export const loadFleetLive = apiThunk<any, AgencyArg>("fleet/loadLive", {
  server: "login",
  url: ({ agencyId }) => `tenant-agencies/${agencyId}/fleet/live`,
  method: "GET",
});

export type FleetStatus = "active" | "busy" | "idle" | "inactive";

interface FleetState {
  /** The fleet register — documents, expiry, assigned driver. */
  vehicles: any[];
  /** Live positions, keyed by vehicle id so socket updates can patch in place. */
  live: Record<string, any>;
  errors: any;
  loading: boolean;
  liveLoading: boolean;
  lastMutation: string | null;
}

const initialState: FleetState = {
  vehicles: [],
  live: {},
  errors: null,
  loading: false,
  liveLoading: false,
  lastMutation: null,
};

const toLiveMap = (rows: any[]): Record<string, any> =>
  (rows ?? []).reduce((acc: Record<string, any>, row: any) => {
    const key = String(row.vehicle_id ?? row.id);
    acc[key] = row;
    return acc;
  }, {});

const fleetSlice = createSlice({
  name: "fleet",
  initialState,
  reducers: {
    /**
     * Patch one car's position from a socket broadcast. Merged rather than
     * replaced so a location-only event doesn't wipe the driver/vehicle fields
     * that came with the initial REST payload.
     */
    updateFleetPosition: (state, action: PayloadAction<any>) => {
      const row = action.payload;
      const key = String(row?.vehicle_id ?? row?.id ?? "");
      if (!key) return;
      state.live[key] = { ...(state.live[key] ?? {}), ...row };
    },
    clearFleetMutation: (state) => {
      state.lastMutation = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadVehicles.pending, (state) => {
        state.errors = null;
        state.loading = true;
      })
      .addCase(loadVehicles.fulfilled, (state, action) => {
        state.vehicles = action.payload?.data ?? [];
        state.loading = false;
      })
      .addCase(loadVehicles.rejected, (state, action) => {
        state.errors = action.payload;
        state.loading = false;
      })

      .addCase(addVehicle.pending, (state) => {
        state.errors = null;
        state.loading = true;
      })
      .addCase(addVehicle.fulfilled, (state) => {
        state.loading = false;
        state.lastMutation = "added";
      })
      .addCase(addVehicle.rejected, (state, action) => {
        state.errors = action.payload;
        state.loading = false;
      })

      .addCase(removeVehicle.pending, (state) => {
        state.errors = null;
        state.loading = true;
      })
      .addCase(removeVehicle.fulfilled, (state) => {
        state.loading = false;
        state.lastMutation = "removed";
      })
      .addCase(removeVehicle.rejected, (state, action) => {
        state.errors = action.payload;
        state.loading = false;
      })

      .addCase(loadFleetLive.pending, (state) => {
        state.liveLoading = true;
      })
      .addCase(loadFleetLive.fulfilled, (state, action) => {
        state.live = toLiveMap(action.payload?.data);
        state.liveLoading = false;
      })
      .addCase(loadFleetLive.rejected, (state, action) => {
        state.errors = action.payload;
        state.liveLoading = false;
      });
  },
});

export const { updateFleetPosition, clearFleetMutation } = fleetSlice.actions;
export default fleetSlice.reducer;
